import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMediaQuery, Grid2, Typography, Box } from '@mui/material';
import { useTranslation } from 'core/contexts/localization';
import { useBookings } from 'core/contexts/booking';

import { ExperiencerService } from 'core/api/services/experiencer';

import { isEmpty } from 'core/packages/utils';
import { BasicTable, PaginationControlled, PreviewExperienceDetail, useDebounce, DateFormattedUtils, MoneyFormattedUtils } from '@utriper/core-ui';
import { BookingStatus, BookingOrigin } from '@utriper/core';
import { PreviewService } from 'core/api/services/preview';

const GlobalBookingsTable = ({ experiencesId, email, bookingCode, showPreviousBookings, load, resources, dateSearch }) => {
    const { t: translate } = useTranslation();
    const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const history = useHistory();
    const { BOOKINGS_PER_PAGE, page, setPage, orderBy, setOrderBy, direction, setDirection } = useBookings();

    // pagination variables
    const [bookings, setBookings] = useState([]);
    const [totalBookings, setTotalBookings] = useState(0);
    const [firstSrvCall, setFirstSrvCall] = useState(true);
    const emailDebounce = useDebounce(email, 300);
    const searchDebounce = useDebounce(bookingCode, 300);
    useEffect(() => {
        setFirstSrvCall(true);
    }, [showPreviousBookings, email, bookingCode]);

    useEffect(() => {
        if (experiencesId && experiencesId.length > 0) {
            ExperiencerService.getBookings(experiencesId, emailDebounce, searchDebounce, showPreviousBookings, page, BOOKINGS_PER_PAGE, orderBy, direction, resources, dateSearch).then((res) => {
                const bookings = prettifyBookings(res.bookings);
                setBookings(bookings);
                setTotalBookings(res.totalBookings);
                setFirstSrvCall(false);
            });
        } else {
            setBookings([]);
            setTotalBookings(0);
        }
    }, [experiencesId, emailDebounce, searchDebounce, showPreviousBookings, page, load, orderBy, direction, resources, dateSearch]);

    const columns = [
        {
            align: 'left',
            val: 'codeFormatted',
            label: translate('bookings.booking_code'),
            column: 'ci_code',
        },
        { val: 'triper', label: translate('bookings.triper'), column: 'ci_first_name' },
        { val: 'email', label: translate('bookings.email'), column: 'ci_email' },
        { val: 'mobilePhone', label: translate('bookings.phone') },
        {
            val: 'experienceName',
            label: translate('bookings.experience_name'),
            column: 'ci_experience_detail_id',
        },
        {
            align: 'center',
            val: 'experienceDetailId',
            label: 'Experience Option',
            column: 'ci_experience_detail_id',
            render: (value, row) => value,
        },
        { val: 'dateFormatted', label: translate('bookings.activity_date'), column: 'ci_date_utc' },
        {
            val: 'resourcesFormatted',
            label: translate('bookings.resources'),
        },
        { val: 'quantity', label: translate('bookings.quantity'), column: 'ci_quantity' },
        {
            val: 'paidPriceFormatted',
            label: translate('bookings.paid_ammount'),
            column: 'ci_paidPrice',
        },
        { val: 'priceFormatted', label: translate('bookings.price'), column: 'ci_paidPrice' },
        { align: 'center', val: 'originString', label: translate('bookings.origin_id') },
        {
            align: 'center',
            val: 'statusString',
            label: translate('bookings.status'),
            column: 'ci_status',
        },
        {
            align: 'center',
            val: 'checkinString',
            label: translate('bookings.checkin'),
            // column: 'ci_status',
        },
        {
            align: 'center',
            val: 'additional',
            label: translate('bookings.additional'),
            style: { width: '120px' },
        },
        {
            align: 'center',
            val: 'resellerBookingReference',
            label: translate('bookings.reseller_code'),
            column: 'ci_reseller_booking_reference',
            style: { minWidth: '150px' },
        },
    ];

    // const resourcesRawData = [
    //     { id: 1, name: 'Pantalon blanco' },
    //     { id: 2, name: 'Salon 1' },
    // ];
    const formattedDataResources = (rawData) => {
        if (!rawData) return null;
        const names = rawData.map((item) => item.name);
        return names.join(', ');
    };

    const prettifyBookings = (bookings = []) => {
        return bookings.map((booking) => {
            return {
                ...booking,
                triper: `${booking.firstName} ${booking.lastName}`,
                statusString: BookingStatus.getLabel(booking.status),
                originString: BookingOrigin.getLabel(booking.originId),
                dateFormatted: DateFormattedUtils(booking.dateUTC, null, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    timeZone: booking.timezone || 'UTC',
                }),
                codeFormatted: booking.code.length > 6 ? booking?.code?.slice(0, 6) : booking.code,
                priceFormatted: `${booking.currency} ${MoneyFormattedUtils(booking.price)}`,
                resourcesFormatted: !isEmpty(booking.resources) ? formattedDataResources(booking.resources) : '',
                paidPriceFormatted: `${booking.currency} ${MoneyFormattedUtils(booking.paidPrice)}`,
                checkinString: booking.checkIn === true ? translate('checkin.completed') : booking.checkIn === false ? translate('checkin.pending') : translate('checkin.na'),
                experienceDetailId: <PreviewExperienceDetail id={booking?.experienceDetailId} label="description1" previewService={PreviewService} />,
                additional: (
                    <Box sx={{ maxWidth: '120px' }}>
                        <Typography noWrap>{booking?.additional}</Typography>
                    </Box>
                ),
            };
        });
    };

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const handleSort = (column) => {
        if (orderBy === column) {
            setDirection(direction === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(column);
            setDirection('asc');
        }
    };
    const onClick = (event, id) => {
        history.push(`/booking/${id}`);
    };

    return (
        <React.Fragment>
            <BasicTable columns={columns} values={bookings} handleSort={handleSort} orderBy={orderBy} direction={direction} onClick={onClick} />
            <br />
            <Grid2 container alignItems="flex-end">
                <Grid2 size={{ xs: 1, sm: 7 }}></Grid2>
                <Grid2 size={{ xs: 11, sm: 5 }}>{totalBookings / BOOKINGS_PER_PAGE < 1 ? null : <PaginationControlled count={Math.ceil(totalBookings / BOOKINGS_PER_PAGE)} page={page} onChange={handlePageChange} totalItems={totalBookings} itemsPerPage={BOOKINGS_PER_PAGE} />}</Grid2>
            </Grid2>
        </React.Fragment>
    );
};

export default GlobalBookingsTable;
